import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CalendarEvent, CalendarEventParticipant } from '../../services/definitions.service';
import { NotiBottomSheetComponent, NotiSheetData, NotiType } from '../../noti-bottom-sheet/noti-bottom-sheet.component';
import { DataModelService } from '../../services/data-model.service';
import { CommonFunctionsService } from '../../services/common-functions.service';
import {
  CalendarEventUnregisterDialogComponent,
  UnregisterDialogResult,
} from './calendar-event-unregister-dialog.component';
import {
  CalendarEventParticipantsDialogComponent,
} from './calendar-event-participants-dialog.component';
import {
  CalendarEventStatisticsDialogComponent,
} from './calendar-event-statistics-dialog.component';
import firebase from 'firebase/app';
import 'firebase/firestore';

export interface CalendarDayEventsDialogData {
  date: Date;
  events: CalendarEvent[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canManageParticipants?: boolean;
  canViewStatistics?: boolean;
  userId?: string;
  userFirstName?: string;
  userLastName?: string;
}

export interface CalendarDayEventsDialogResult {
  action: 'create' | 'edit' | 'delete' | 'close';
  event?: CalendarEvent;
}

interface EventRegistrationState {
  loading: boolean;
  registered: boolean;
  participantPath?: string;
  processing: boolean;
}

@Component({
  selector: 'app-calendar-day-events-dialog',
  templateUrl: './calendar-day-events-dialog.component.html',
  styleUrls: ['./calendar-day-events-dialog.component.css'],
})
export class CalendarDayEventsDialogComponent implements OnInit {
  /** Tracks registration state per event id. */
  registrationStates: { [eventId: string]: EventRegistrationState } = {};

  private readonly dayFormatter = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
  });
  private readonly dateOnlyFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  private readonly fullDateFormatter = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  private readonly shortDateFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  private readonly timeFormatter = new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  constructor(
    public dialogRef: MatDialogRef<CalendarDayEventsDialogComponent>,
    private bottomSheet: MatBottomSheet,
    private dialog: MatDialog,
    private dataModel: DataModelService,
    private commonFunctions: CommonFunctionsService,
    @Inject(MAT_DIALOG_DATA) public data: CalendarDayEventsDialogData
  ) {}

  ngOnInit(): void {
    this.loadRegistrationStates();
  }

  formatFullDate(date: Date): string {
    return this.fullDateFormatter.format(new Date(date));
  }

  formatDay(date: Date): string {
    return this.dayFormatter.format(new Date(date));
  }

  formatDateOnly(date: Date): string {
    return this.dateOnlyFormatter.format(new Date(date));
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'meeting':
        return '#1a73e8';
      case 'task':
        return '#34a853';
      case 'reminder':
        return '#fbbc04';
      default:
        return '#5f6368';
    }
  }

  formatShortDate(date: Date): string {
    return this.shortDateFormatter.format(this.asDate(date));
  }

  formatTime(date: Date): string {
    return `${this.timeFormatter.format(this.asDate(date))} Uhr`;
  }

  asDate(value: any): Date {
    if (value?.toDate) {
      return value.toDate();
    }

    return new Date(value);
  }

  onClose(): void {
    this.dialogRef.close({ action: 'close' } as CalendarDayEventsDialogResult);
  }

  onCreate(): void {
    if (!this.data.canCreate) {
      return;
    }
    this.dialogRef.close({ action: 'create' } as CalendarDayEventsDialogResult);
  }

  /**
   * Opens the participants management dialog for an event.
   */
  onManageParticipants(event: CalendarEvent): void {
    if (!this.data.canManageParticipants) {
      return;
    }

    this.dialog.open(CalendarEventParticipantsDialogComponent, {
      width: '540px',
      maxWidth: '95vw',
      height: '60vh',
      autoFocus: false,
      data: { event },
    });
  }

  /**
   * Opens the statistics dialog for an event.
   */
  onViewStatistics(event: CalendarEvent): void {
    if (!this.data.canViewStatistics) {
      return;
    }

    this.dialog.open(CalendarEventStatisticsDialogComponent, {
      width: '540px',
      maxWidth: '95vw',
      height: '60vh',
      autoFocus: false,
      data: { event },
    });
  }

  openNavigation(location: string, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    const query = encodeURIComponent(location);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  }

  onEdit(event: CalendarEvent): void {
    if (!this.data.canEdit) {
      return;
    }

    this.dialogRef.close({
      action: 'edit',
      event,
    } as CalendarDayEventsDialogResult);
  }

  onDelete(event: CalendarEvent): void {
    if (!this.data.canDelete) {
      return;
    }

    const sheetRef = this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(
        `Möchtest du das Event "${event?.name || 'Unbenannt'}" wirklich löschen?`,
        NotiType.Warn,
        0,
        true,
        true,
        'Löschen',
        'Abbrechen'
      ),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });

    sheetRef.afterDismissed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.dialogRef.close({
        action: 'delete',
        event,
      } as CalendarDayEventsDialogResult);
    });
  }

  /**
   * Returns the registration state for an event.
   */
  getRegistrationState(event: CalendarEvent): EventRegistrationState {
    const eventId = event?.id || '';
    if (!this.registrationStates[eventId]) {
      this.registrationStates[eventId] = {
        loading: true,
        registered: false,
        processing: false,
      };
    }
    return this.registrationStates[eventId];
  }

  /**
   * Checks whether a user can register for events (requires userId in dialog data).
   * Registration is disabled for past events.
   */
  canRegister(): boolean {
    return !!this.data.userId;
  }

  /**
   * Checks whether an event has already ended.
   */
  isEventPast(event: CalendarEvent): boolean {
    if (!event?.end) {
      return false;
    }
    const endDate = event.end.toDate ? event.end.toDate() : new Date(event.end as any);
    return endDate < new Date();
  }

  /**
   * Registers the current user for a calendar event.
   */
  async onRegister(event: CalendarEvent): Promise<void> {
    if (!this.data.userId || !event?.path) {
      return;
    }

    const state = this.getRegistrationState(event);
    if (state.processing || state.registered) {
      return;
    }

    state.processing = true;

    // Check if there's an existing participant doc (e.g. previously unregistered)
    const existing = await this.dataModel.getCalendarEventParticipantByUserId(
      event.path,
      this.data.userId
    );

    let success = false;

    if (existing.result && Array.isArray(existing.data) && existing.data.length > 0) {
      // Update existing participant doc back to attending
      const participantRaw = existing.data[0];
      const participantPath = participantRaw.path || participantRaw[('path' as string)];
      if (participantPath) {
        const updateResult = await this.dataModel.updateCalendarEventParticipant(
          participantPath,
          { attending: true, excuseText: '' }
        );
        success = updateResult.result;
        if (success) {
          state.participantPath = participantPath;
        }
      }
    } else {
      // Create new participant doc
      const participantData: CalendarEventParticipant = {
        firstName: this.data.userFirstName || '',
        lastName: this.data.userLastName || '',
        prticipantFirstName: this.data.userFirstName || '',
        participantLastName: this.data.userLastName || '',
        userId: this.data.userId,
        attending: true,
        created: firebase.firestore.Timestamp.fromDate(new Date()),
      };

      const addResult = await this.dataModel.addCalendarEventParticipant(
        event.path,
        participantData
      );
      success = addResult.result;
      if (success && addResult.documentPath) {
        state.participantPath = addResult.documentPath;
      }
    }

    state.processing = false;

    if (success) {
      state.registered = true;
      this.commonFunctions.showSuccessToast('Du wurdest erfolgreich angemeldet!');
    } else {
      this.commonFunctions.showErrorToast('Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
    }
  }

  /**
   * Unregisters the current user from a calendar event after confirmation.
   */
  onUnregister(event: CalendarEvent): void {
    if (!this.data.userId || !event?.path) {
      return;
    }

    const state = this.getRegistrationState(event);
    if (state.processing || !state.registered) {
      return;
    }

    const unregisterDialogRef = this.dialog.open(CalendarEventUnregisterDialogComponent, {
      width: '400px',
      maxWidth: '95vw',
      data: { eventName: event.name || 'Unbenannt' },
    });

    unregisterDialogRef.afterClosed().subscribe(async (result: UnregisterDialogResult) => {
      if (!result?.confirmed || !result.excuseText) {
        return;
      }

      state.processing = true;

      let success = false;

      if (state.participantPath) {
        const updateResult = await this.dataModel.updateCalendarEventParticipant(
          state.participantPath,
          { attending: false, excuseText: result.excuseText }
        );
        success = updateResult.result;
      } else {
        // Participant path not cached, query it
        const existing = await this.dataModel.getCalendarEventParticipantByUserId(
          event.path,
          this.data.userId
        );
        if (existing.result && Array.isArray(existing.data) && existing.data.length > 0) {
          const participantPath = existing.data[0].path;
          if (participantPath) {
            const updateResult = await this.dataModel.updateCalendarEventParticipant(
              participantPath,
              { attending: false, excuseText: result.excuseText }
            );
            success = updateResult.result;
          }
        }
      }

      state.processing = false;

      if (success) {
        state.registered = false;
        this.commonFunctions.showInfoToast('Du wurdest erfolgreich abgemeldet.');
      } else {
        this.commonFunctions.showErrorToast('Abmeldung fehlgeschlagen. Bitte versuche es erneut.');
      }
    });
  }

  /**
   * Loads the registration state for all events in the dialog.
   */
  private async loadRegistrationStates(): Promise<void> {
    if (!this.data.userId || !this.data.events?.length) {
      return;
    }

    const loadPromises = this.data.events.map(async (event) => {
      const eventId = event?.id || '';
      if (!eventId || !event.path) {
        return;
      }

      const state: EventRegistrationState = {
        loading: true,
        registered: false,
        processing: false,
      };
      this.registrationStates[eventId] = state;

      const result = await this.dataModel.getCalendarEventParticipantByUserId(
        event.path,
        this.data.userId
      );

      if (result.result && Array.isArray(result.data) && result.data.length > 0) {
        const participant = this.dataModel.deserializeCalendarEventParticipant(result.data[0]);
        state.registered = participant.attending === true;
        state.participantPath = participant.path;
      }

      state.loading = false;
    });

    await Promise.all(loadPromises);
  }
}
